import React, { FC } from "react";
import { observer } from "mobx-react"; 
import {
  CustomLabel,
  FlexContainer,
  Input,
  IOption,
  SingleSelect,
  ColorPicker as ColorPickerComponent,
} from "@mindmemobile/mm-shared-global";
import { CheckoutBrandingModel, ColorPickerElement, CustomColors } from "../../../shared/models";
import { CheckOutButtonStyle, CheckOutDefaultSegmentType, CheckOutPayButtonCaption } from "../../../shared/enums";
import { ButtonStyleSelector, DescriptionCell, LabelCell, SelectedColorButton } from "../../../shared/styles";


export type ColorPickerType = 'main' | 'border' | 'background' | 'progress';
type IProps = {
    customColors: CustomColors;
    onChange: (field: keyof CheckoutBrandingModel, value: CustomColors) => void;
};

const buttonOptions: IOption[] = Object.keys(CheckOutPayButtonCaption).map((key) => {
    return {
        label: CheckOutPayButtonCaption[key as keyof typeof CheckOutPayButtonCaption],
        value: key,
    };
});

const segmentOptions: IOption[] = Object.keys(CheckOutDefaultSegmentType).map((key) => {
    return {
        label: CheckOutDefaultSegmentType[key as keyof typeof CheckOutDefaultSegmentType],
        value: key,
    };
});

const CheckoutCustomColorSection: FC<IProps> = ({ onChange, customColors }) => {
    // const getColor = (field: CheckOutButtonStyle) => {
    //     const isSelected = field === customColors.buttonStyleType;
    //     return isSelected ? 'red' : 'black';
    // };

    const singleSelect = (
        field: keyof CustomColors,
        selectedKey: string,
        className: string,
        options: IOption[],
        error: boolean,
        errorMessage: string,
    ): React.ReactNode => {
        return (
            <SingleSelect
                selectedKey={selectedKey}
                hasError={error}
                searchable={false}
                nameKeys={['label']}
                valueKeys={['value']}
                options={[options || []]}
                errorMessage={errorMessage}
                onChange={(option: any) => handleCustomColorsInputChange(field, option.value)}
                className={className}
            />
        );
    };

    const textInput = (
        field: keyof CustomColors,
        type: string,
        value: string,
        placeHolder: string,
        className: string,
        error: boolean,
        errorMessage: string,
    ) => {
        return (
            <Input
                type={'text'}
                value={value}
                error={error}
                errorMessage={errorMessage}
                className={`styled-input merchant-inputs ${className}`}
                onChange={(e) => handleCustomColorsInputChange(field, e.target.value)}
                placeholder={placeHolder}
            />
        );
    };

    const handleColorPickerChange = (
        pickerType: ColorPickerType,
        index: number,
        key: keyof ColorPickerElement,
        value: any,
    ) => {
        const updatedColorPickers = [...customColors.colorPickers[pickerType]];
        updatedColorPickers[index] = new ColorPickerElement({
            ...updatedColorPickers[index],
            [key]: value,
        });
        const updatedCustomColorsData = {
            ...customColors,
            colorPickers: {
                ...customColors.colorPickers,
                [pickerType]: updatedColorPickers,
            },
        };
        onChange('customColors', new CustomColors(updatedCustomColorsData));
    };

    const handleCustomColorsInputChange = (field: keyof CustomColors, value: string | boolean) => {
        const updatedCustomColorsData = new CustomColors({ ...customColors, [field]: value });
        onChange('customColors', updatedCustomColorsData);
    };

    const renderColorPickerElements = (pickerType: ColorPickerType, label: string) => {
        const colorPickers = customColors.colorPickers[pickerType];
        return (
            <FlexContainer className="label-description-row">
                <LabelCell>
                    <CustomLabel className="standard-lh">{label}</CustomLabel>
                </LabelCell>
                <DescriptionCell>
                    <FlexContainer fxWrap="wrap" className="position-relative" spacing={9}>
                        {colorPickers.map((picker, index) => (
                            <div key={index} className="colored-block-div">
                                <FlexContainer spacing={10} alignItems="center" className="position-relative">
                                    <SelectedColorButton
                                        heightWidth="24px"
                                        padding="3px"
                                        borderRadius="2px"
                                        onClick={() =>
                                            handleColorPickerChange(
                                                pickerType,
                                                index,
                                                'showColorPicker',
                                                !picker.showColorPicker,
                                            )
                                        }
                                        color={picker.color?.rgba}
                                        className="color-picker-gallery"
                                    >
                                        <div />
                                    </SelectedColorButton>
                                    {picker.showColorPicker && (
                                        <ColorPickerComponent
                                            color={picker.deserializeColor()}
                                            onChange={(color) =>
                                                handleColorPickerChange(pickerType, index, 'color', color)
                                            }
                                            onHide={() =>
                                                handleColorPickerChange(pickerType, index, 'showColorPicker', false)
                                            }
                                            className="checkout-color-picker"
                                        />
                                    )}
                                    <CustomLabel>{picker.name}</CustomLabel>
                                </FlexContainer>
                            </div>
                        ))}
                    </FlexContainer>
                </DescriptionCell>
            </FlexContainer>
        );
    };

    const renderCustomColorsContent = () => {
        return (
            <FlexContainer fxLayout="column">
                <div>{renderColorPickerElements('main', 'Main')}</div>
                <div>{renderColorPickerElements('background', 'Background')}</div>
                <div>{renderColorPickerElements('border', 'Border')}</div>
                <div>{renderColorPickerElements('progress', 'Progress')}</div>
                <FlexContainer className="label-description-row">
                    <LabelCell>
                        <CustomLabel className="standard-lh">Button Styles</CustomLabel>
                    </LabelCell>
                    <DescriptionCell>
                        <FlexContainer spacing={20}>
                            <ButtonStyleSelector
                                pillShape
                                isActive={customColors.buttonStyleType === CheckOutButtonStyle.Round}
                                onClick={() =>
                                    handleCustomColorsInputChange('buttonStyleType', CheckOutButtonStyle.Round)
                                }
                            />
                            <ButtonStyleSelector
                                roundedRect
                                isActive={customColors.buttonStyleType === CheckOutButtonStyle.SquareRoundCorner}
                                onClick={() =>
                                    handleCustomColorsInputChange(
                                        'buttonStyleType',
                                        CheckOutButtonStyle.SquareRoundCorner,
                                    )
                                }
                            />
                            <ButtonStyleSelector
                                isActive={customColors.buttonStyleType === CheckOutButtonStyle.Square}
                                onClick={() =>
                                    handleCustomColorsInputChange('buttonStyleType', CheckOutButtonStyle.Square)
                                }
                            />
                        </FlexContainer>
                    </DescriptionCell>
                </FlexContainer>
                <FlexContainer className="label-description-row">
                    <LabelCell>
                        <CustomLabel>Button Caption</CustomLabel>
                    </LabelCell>
                    <DescriptionCell>
                        <FlexContainer alignItems="center" spacing={15}>
                            {singleSelect(
                                'payButtonType',
                                customColors.payButtonType,
                                'dpdn-no-separator btn-caption-dpdn',
                                buttonOptions,
                                customColors.hasError('payButtonType'),
                                '',
                            )}
                            {customColors.payButtonType === CheckOutPayButtonCaption.Custom &&
                                textInput(
                                    'buttonCustomCaption',
                                    'text',
                                    customColors.buttonCustomCaption,
                                    'Enter caption',
                                    'custom-caption-input',
                                    customColors.hasError('buttonCustomCaption'),
                                    '',
                                )}
                        </FlexContainer>
                    </DescriptionCell>
                </FlexContainer>
                <FlexContainer className="label-description-row">
                    <LabelCell>
                        <CustomLabel>Default Segment</CustomLabel>
                    </LabelCell>
                    <DescriptionCell>
                        {singleSelect(
                            'defaultSegment',
                            customColors.defaultSegment,
                            'dpdn-no-separator checkout-segment-dpdn',
                            segmentOptions,
                            customColors.hasError('defaultSegment'),
                            '',
                        )}
                    </DescriptionCell>
                </FlexContainer>
            </FlexContainer>
        );
    };

    return (
        <FlexContainer fxLayout="column" spacing={30} className="option-checkout">
            {renderCustomColorsContent()}
        </FlexContainer>
    );
};

export default observer(CheckoutCustomColorSection);
